/**
 * Service for fetching AI models from various providers
 */

import { Model } from '../types';

const API_BASE_URL = 'http://localhost:5001/api';

export interface ModelsResponse {
  success: boolean;
  data: {
    openai: Model[];
    gemini: Model[];
    openrouter: Model[];
  };
  message?: string;
  error?: string;
}

export interface ProviderModelsResponse {
  success: boolean;
  data: Model[];
  provider: string;
  count: number;
  error?: string;
  message?: string;
}

export interface SearchModelsResponse {
  success: boolean;
  data: Model[];
  query: string;
  count: number;
  error?: string;
  message?: string;
}

class ModelsService {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private isCacheValid(key: string): boolean {
    const cached = this.cache.get(key);
    if (!cached) return false;
    return Date.now() - cached.timestamp < this.CACHE_TTL;
  }

  private setCache(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  private getCache(key: string): any {
    const cached = this.cache.get(key);
    return cached?.data;
  }

  /**
   * Fetch all models from all providers
   */
  async getAllModels(apiKeys?: {
    openai?: string;
    gemini?: string;
  }): Promise<ModelsResponse> {
    const cacheKey = `all-models-${JSON.stringify(apiKeys || {})}`;
    
    if (this.isCacheValid(cacheKey)) {
      return this.getCache(cacheKey);
    }

    try {
      const params = new URLSearchParams();
      if (apiKeys?.openai) params.append('openai_key', apiKeys.openai);
      if (apiKeys?.gemini) params.append('gemini_key', apiKeys.gemini);

      const response = await fetch(`${API_BASE_URL}/models/list?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ModelsResponse = await response.json();
      
      if (result.success) {
        this.setCache(cacheKey, result);
      }
      
      return result;
    } catch (error) {
      console.error('Error fetching all models:', error);
      return {
        success: false,
        data: { openai: [], gemini: [], openrouter: [] },
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Fetch models from a specific provider
   */
  async getProviderModels(
    provider: 'openai' | 'gemini' | 'openrouter',
    apiKey?: string
  ): Promise<ProviderModelsResponse> {
    const cacheKey = `${provider}-models-${apiKey || 'no-key'}`;
    
    if (this.isCacheValid(cacheKey)) {
      return this.getCache(cacheKey);
    }

    try {
      const params = new URLSearchParams();
      if (apiKey) params.append('api_key', apiKey);

      const response = await fetch(`${API_BASE_URL}/models/provider/${provider}?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ProviderModelsResponse = await response.json();
      
      if (result.success) {
        this.setCache(cacheKey, result);
      }
      
      return result;
    } catch (error) {
      console.error(`Error fetching ${provider} models:`, error);
      return {
        success: false,
        data: [],
        provider,
        count: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Search models across all providers
   */
  async searchModels(
    query: string,
    options?: {
      provider?: 'openai' | 'gemini' | 'openrouter';
      apiKeys?: {
        openai?: string;
        gemini?: string;
      };
    }
  ): Promise<SearchModelsResponse> {
    if (!query.trim()) {
      return {
        success: false,
        data: [],
        query,
        count: 0,
        error: 'Search query is required'
      };
    }

    try {
      const params = new URLSearchParams();
      params.append('q', query);
      if (options?.provider) params.append('provider', options.provider);
      if (options?.apiKeys?.openai) params.append('openai_key', options.apiKeys.openai);
      if (options?.apiKeys?.gemini) params.append('gemini_key', options.apiKeys.gemini);

      const response = await fetch(`${API_BASE_URL}/models/search?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: SearchModelsResponse = await response.json();
      return result;
    } catch (error) {
      console.error('Error searching models:', error);
      return {
        success: false,
        data: [],
        query,
        count: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Refresh models cache
   */
  async refreshModels(apiKeys?: {
    openai?: string;
    gemini?: string;
  }): Promise<ModelsResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/models/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiKeys || {}),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ModelsResponse = await response.json();
      
      if (result.success) {
        // Clear local cache
        this.cache.clear();
      }
      
      return result;
    } catch (error) {
      console.error('Error refreshing models:', error);
      return {
        success: false,
        data: { openai: [], gemini: [], openrouter: [] },
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Clear local cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Export singleton instance
export const modelsService = new ModelsService();
export default modelsService;
